import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import type { WorkOrderId } from "../lib/workIds";

const uploadSessionResponseSchema = z.object({
  uploadId: z.string().uuid(),
  uploadUrl: z.string().url().nullable(),
  isUploaded: z.boolean(),
  expiresIn: z.number(),
});
const completeUploadResponseSchema = z.object({
  uploadId: z.string().uuid(),
  uploadStatus: z.string(),
  scanStatus: z.string(),
});

export type ProcedurePhotoUploadSession = z.infer<
  typeof uploadSessionResponseSchema
>;

/**
 * A Procedure photo upload step the API rejected. `status` is the HTTP
 * status, or null when the client stopped the request first.
 */
export class ProcedurePhotoRequestError extends Error {
  constructor(
    message: string,
    readonly status: number | null = null,
  ) {
    super(message);
    this.name = "ProcedurePhotoRequestError";
  }
}

export type ProcedurePhotoScope = {
  customerId: string;
  facilityId: string;
  workOrderId: WorkOrderId;
};

export type ProcedurePhotoUploadInput = {
  uploadId: string;
  fileName: string;
  contentType: string;
  contentLength: number;
  checksumSha256: string;
};

function uploadPath({
  customerId,
  facilityId,
  workOrderId,
}: ProcedurePhotoScope) {
  return `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}/work-orders/${encodeURIComponent(workOrderId)}/procedure/photos/upload-url`;
}

async function errorMessage(response: Response) {
  const body: unknown = await response.json().catch(() => null);
  if (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof body.error === "string"
  )
    return body.error;
  return `Procedure photo request failed (${response.status}).`;
}

async function postJson<T>(path: string, schema: z.ZodType<T>, body: unknown) {
  const response = await workApiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok)
    throw new ProcedurePhotoRequestError(
      await errorMessage(response),
      response.status,
    );
  return schema.parse(await response.json());
}

/** Stages a photo upload and returns the presigned PUT URL for its bytes. */
export function createProcedurePhotoUpload(
  scope: ProcedurePhotoScope,
  input: ProcedurePhotoUploadInput,
) {
  return postJson(uploadPath(scope), uploadSessionResponseSchema, input);
}

/** Re-signs the PUT URL for the same upload session; no new file is staged. */
export function retryProcedurePhotoUpload(
  scope: ProcedurePhotoScope,
  uploadId: string,
) {
  return postJson(`${uploadPath(scope)}/retry`, uploadSessionResponseSchema, {
    uploadId,
  });
}

/** Asks the server to verify the uploaded bytes against the staged session. */
export function completeProcedurePhotoUpload(
  scope: ProcedurePhotoScope,
  uploadId: string,
) {
  return postJson(
    `${uploadPath(scope)}/completion`,
    completeUploadResponseSchema,
    { uploadId },
  );
}
